import Loader from 'Components/Common/Loader';
import InfluencerCardHeader from 'Components/influencer/InfluencerCardHeader';
import { PRIVILEGE } from 'constants/privilege';
import { usePrivileges } from 'hooks/useAuth';
import { campaignIconSVG } from 'pages/Campaigns/Campaign';
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Modal, Spinner } from 'reactstrap';
import { fetchApplicationCost, processApplication as processApplicationAPI } from 'services/api/campaign/application';
import { updateApplicationStatus } from 'slices/campaigns/applications/reducer';
import { updateRemainingBudget } from 'slices/campaigns/reducer';
import { toggleApplicationOffcanvas } from 'slices/ui/reducer';
import { AppDispatch, RootState, store } from 'store';
import Swal from 'sweetalert2';
import { ApplicationCost, CampaignApplication } from 'types/campaign/applications';
import { getErrorMessage, textToTitleCase } from 'utils/helpers';
interface ApproveApplicationCostProps {
    application: CampaignApplication;
    campaignId: string;
    isOpen: boolean;
    onClose: () => void;
}
const ApproveApplicationCost: React.FC<ApproveApplicationCostProps> = ({ application, campaignId, isOpen, onClose }) => {
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const [cost, setCost] = useState<ApplicationCost | null>(null);
    const [fetchingCost, setFetchingCost] = useState<boolean>(false);
    const [approving, setApproving] = useState<boolean>(false);

    const [canApprove, setCanApprove] = useState<boolean>(false);
    const [showBreakup, setShowBreakup] = useState<boolean>(false);
    const dispatch: AppDispatch = useDispatch();
    const privileges = usePrivileges();

    useEffect(() => {
        if (cost) {
            setCanApprove((cost.wallet_balance ?? 0) >= (cost.cost ?? 0));
        }
    }, [cost])

    useEffect(() => {
        if (isOpen && application) {
            fetchCost();
        }
    }, [application, campaignId, selectedAgencyId, isOpen]);

    const fetchCost = async () => {
        if (!campaignId || !application?.influencer?.uuid || !selectedAgencyId) return;
        setFetchingCost(true);
        try {
            const cost = await fetchApplicationCost(selectedAgencyId, campaignId, application.influencer.uuid);
            setCost(cost);
        } catch (error) {
            console.log(error);
        } finally {
            setFetchingCost(false);
        }
    }

    const approveApplication = async () => {
        await processApplication(application.influencer.uuid, 'accept');
        dispatch(toggleApplicationOffcanvas(null))
    }

    const processApplication = async (influencerId: string, action: 'accept' | 'reject', force: boolean = false) => {
        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process application!");
            return;
        }
        try {
            // setApplicationStatus('processing');
            setApproving(true)
            dispatch(updateApplicationStatus({ campaignId, applicationId: application.influencer.uuid, status: 'processing' }));
            const agencyId = store.getState().auth.selectedAgencyId;
            if (!agencyId || !cost) return;
            const result = await processApplicationAPI(agencyId, campaignId, influencerId, { action, force, cost });
            if (result.status && result.application?.status) {
                // setApplicationStatus(result.application?.status)
                dispatch(updateApplicationStatus({ campaignId, applicationId: application.influencer.uuid, status: result.application.status }));
            }
            if (result.campaign?.remaining_budget) {
                dispatch(updateRemainingBudget({ campaignId, budget: result.campaign.remaining_budget }));
            }
            onClose();
        } catch (error: any) {
            if (error?.response?.data?.error_code === "existing_ongoing_collabs") {
                const r = await Swal.fire({
                    title: error?.response?.data?.message ?? "This influencer already has ongoing collaborations in other campaigns",
                    iconHtml: campaignIconSVG,
                    showCancelButton: true,
                    confirmButtonText: "Approve Anyway",
                    cancelButtonText: "Cancel",
                    reverseButtons: true
                });

                if (r.isConfirmed) {
                    try {
                        return processApplication(influencerId, action, true);
                    } catch (error: any) {
                        console.log(error);
                    }
                }
            } else {
                toast.error(getErrorMessage(error, "Failed to process application!"));
            }
            // setApplicationStatus('pending');
            dispatch(updateApplicationStatus({ campaignId, applicationId: application.influencer.uuid, status: 'pending' }));
            console.log(error);
        } finally {
            setApproving(false);
        }
    }

    if (!application) return null;

    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className='application-cost-approval-modal'>
            {
                fetchingCost ? (
                    <div className="modal-body d-flex align-items-center justify-content-center" style={{ height: '400px' }}>
                        <div className="d-flex flex-column align-items-center justify-content-center">
                            <Spinner color='primary' />
                            <span className='mt-2'>Loading...</span>
                        </div>
                    </div>) : (
                    <div className="modal-body">
                        <h6 className='application-cost-approval-modal-title mb-3'>Are you sure you want to approve this application?</h6>
                        <InfluencerCardHeader name={application.influencer?.name ?? (application?.social_account?.name ?? `@${application.social_account?.handle}`)} padding='p-0' hideAdditionalInfo={true} borderBottom={false} socialAccount={application.social_account} gender={application?.influencer?.gender_name} location={application?.influencer?.location} language={application?.language?.name} />
                        <p className='mt-3'>Approving will block the required funds from your brand wallet until the creator completes delivery.</p>
                        <div className="block-amount-card">
                            <div className="d-flex justify-content-between">
                                <div className='w-50'>
                                    <div className="d-flex flex-column justify-content-between gap-5">
                                        <div>
                                            <span className='title'>Amount to be Blocked</span>
                                            <h6 className="block-amount">{cost ? `₹${cost.cost}` : ''}</h6>

                                            <button className="btn btn-link text-primary p-0" style={{ marginTop: '6px' }} onClick={() => setShowBreakup(!showBreakup)}> {showBreakup ? 'Hide Breakup' : 'View Breakup'}</button>
                                        </div>
                                        <div>
                                            <span className='wallet-balance-label'>Your Wallet Balance: <span className={`ms-2 wallet-balance-value ${(cost?.wallet_balance ?? 0) < (cost?.cost ?? 0) ? 'text-danger' : 'text-success'}`}>{cost ? `₹${cost.wallet_balance}` : ''}</span></span>
                                        </div>
                                    </div>

                                </div>
                                {
                                    showBreakup ? (
                                        <div className="border-left-1 w-50 px-3">
                                            <h6>
                                                Blocked Amount Breakup
                                            </h6>
                                            <div className="d-flex flex-column justify-content-between gap-2 breakup-list">
                                                <div className="d-flex flex-column gap-2">
                                                    {
                                                        (cost?.breakup_items ?? []).map((item, index) => (
                                                            <div className="d-flex align-items-center justify-content-between" key={index}>
                                                                <span className="breakup-item-label">{textToTitleCase(item.name)}</span>
                                                                <span className='breakup-item-value'>{item.value ? `₹${item.value}` : ''}</span>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                                {/* <div className="border-top-1 pt-2">
                                                    <div className="d-flex align-items-center justify-content-between">
                                                        <span className="breakup-item-label">Total Amount To Be Blocked</span>
                                                        <span className='breakup-item-value'>{cost ? `₹${cost.cost}` : ''}</span>
                                                    </div>
                                                </div> */}
                                            </div>
                                        </div>
                                    ) : null
                                }
                            </div>

                        </div>
                        <div className="mt-3">
                            {
                                (!canApprove) ? (
                                    <div className="custom-alert custom-alert-danger" role="alert">
                                        You're short by <span className={`fw-bold text-danger`}> {`₹${(cost?.cost ?? 0) - (cost?.wallet_balance ?? 0)}`} </span> Please add funds to continue.
                                    </div>
                                ) : <div className="custom-alert custom-alert-info">
                                    Funds are reserved, not deducted, and auto-release once the creator delivers.
                                </div>
                            }
                        </div>
                        <div className="mt-4 d-flex justify-content-between align-items-center gap-4">
                            <button className="btn btn-gradient-outline w-50" onClick={onClose}>CANCEL</button>
                            {
                                (canApprove) ? (
                                    <button disabled={approving} className="btn btn-gradient w-50" onClick={approveApplication}>{approving ? 'APPROVING...' : 'APPROVE'}</button>
                                ) : (
                                    <Link target='_blank' to={{ pathname: '/wallet' }} onClick={() => onClose()} className="btn btn-gradient w-50">ADD FUND</Link>
                                )
                            }
                        </div>
                    </div>
                )
            }
        </Modal>
    )
}

export default ApproveApplicationCost
